import { useState, useEffect } from 'react';
import { Input, Button, Typography, Space, Tooltip } from 'antd';
import { DeleteOutlined, PlusOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';

const { Text } = Typography;

// One topic row: edits save on blur/Enter, and follow the server value when
// it changes underneath (same pattern as GroupEditor's name field).
function TopicText({ value, onSave, disabled }) {
    const [draft, setDraft] = useState(value);
    useEffect(() => setDraft(value), [value]);
    const commit = () => {
        const text = draft.replace(/\s+/g, ' ').trim();
        if (!text) { setDraft(value); return; }
        if (text !== value) onSave(text);
    };
    return (
        <Input
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onBlur={commit}
            onPressEnter={e => e.target.blur()}
            variant="borderless"
            maxLength={280}
            disabled={disabled}
            className="gathering-topic-input"
        />
    );
}

/**
 * Admin: conversation topics, one sentence each. Room 1 gets the first,
 * room 2 the second, and so on — continuing into the next breakout round, and
 * looping back to the first when there are more rooms than topics. The note under the list says how this round of
 * rooms uses them. Pasting several lines adds one topic per line.
 */
function TopicEditor({ topics, roomCount, roundCount = 1, readOnly, onCreate, onUpdate, onDelete, onReorder }) {
    const [newText, setNewText] = useState('');

    const addLines = (text) => {
        const lines = text.split(/\r?\n/).map(l => l.replace(/\s+/g, ' ').trim()).filter(Boolean);
        lines.reduce((p, line) => p.then(() => onCreate(line)), Promise.resolve()).catch(() => {});
        setNewText('');
    };
    const move = (i, by) => {
        const ids = topics.map(t => t.id);
        [ids[i], ids[i + by]] = [ids[i + by], ids[i]];
        onReorder(ids);
    };

    // roomCount counts rooms across every breakout round — topics carry on
    // from one round to the next rather than restarting.
    const rooms = roundCount > 1 ? `${roomCount} rooms across ${roundCount} rounds` : `${roomCount} rooms`;
    const note = topics.length === 0 || roomCount === 0 ? null
        : roomCount > topics.length
            ? `${rooms}, ${topics.length} topics — topics repeat from room ${topics.length + 1}.`
            : topics.length > roomCount
                ? `${rooms}, ${topics.length} topics — the last ${topics.length - roomCount} ${topics.length - roomCount === 1 ? 'isn’t' : 'aren’t'} used.`
                : `${rooms}, ${topics.length} topics — one each.`;

    return (
        <div className="gathering-topics">
            {topics.length === 0 && (
                <Text type="secondary" style={{ fontSize: 'var(--fs-sm)' }}>
                    No topics yet. Add a few sentences to get each room talking — they're handed out in order.
                </Text>
            )}
            {topics.map((t, i) => (
                <div key={t.id} className="gathering-topic-row">
                    <span className="gathering-topic-num">{i + 1}</span>
                    <TopicText value={t.text} disabled={readOnly} onSave={text => onUpdate(t.id, text)} />
                    {!readOnly && (
                        <Space size={0}>
                            <Tooltip title="Move up">
                                <Button type="text" size="small" icon={<ArrowUpOutlined />} disabled={i === 0} onClick={() => move(i, -1)} />
                            </Tooltip>
                            <Tooltip title="Move down">
                                <Button type="text" size="small" icon={<ArrowDownOutlined />} disabled={i === topics.length - 1} onClick={() => move(i, 1)} />
                            </Tooltip>
                            <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => onDelete(t.id)} />
                        </Space>
                    )}
                </div>
            ))}
            {!readOnly && (
                <Space.Compact style={{ width: '100%' }}>
                    <Input
                        value={newText}
                        onChange={e => setNewText(e.target.value)}
                        onPressEnter={() => addLines(newText)}
                        onPaste={e => {
                            const pasted = e.clipboardData.getData('text');
                            if (/\n/.test(pasted.trim())) { e.preventDefault(); addLines(pasted); }
                        }}
                        placeholder="Add a topic, e.g. What surprised you this week?"
                        maxLength={280}
                    />
                    <Button type="dashed" icon={<PlusOutlined />} onClick={() => addLines(newText)} disabled={!newText.trim()}>
                        Add topic
                    </Button>
                </Space.Compact>
            )}
            {note && <Text type="secondary" style={{ fontSize: 'var(--fs-sm)' }}>{note}</Text>}
        </div>
    );
}

export default TopicEditor;
